// covered-by: frontends/carrie/src/utils/__tests__/http.test.ts
// covered-by: frontends/carrie/src/routes/login/__tests__/LoginView.test.tsx
import { useSyncExternalStore } from "react";

/**
 * The per-enterprise MFA refusal notice.
 *
 * christo refuses a session that carries no MFA proof for the enterprise it is
 * scoped to (401 from the authenticate middleware, 403 from errorHandler, both
 * with `code: "mfa_required"`). The refusal is handled in exactly one place —
 * the http layer (`utils/http.ts`) — which clears the session and sends the
 * user to `/login` with the notice in the URL, because a full page load is what
 * the sign-in flow expects.
 *
 * That URL is what carries the notice across the reload. This slot exists for
 * the one case a redirect cannot serve: a refusal that arrives while the user
 * is already on the login screen, where the http layer deliberately does NOT
 * navigate (reloading /login re-fires the request that just refused, which
 * loops). The notice is then shown in place.
 */

/** The discriminator christo sets on an MFA refusal. */
export const MFA_REQUIRED_CODE = "mfa_required";

/** The value of `?error=` that renders the notice on the login screen. */
export const MFA_REQUIRED_ERROR = "mfa-required";

export type MfaRequiredNotice = {
  /** Session email at the moment of the refusal, for the login form prefill. */
  email: string | null;
};

let notice: MfaRequiredNotice | null = null;
const listeners = new Set<() => void>();

function emit() {
  // forEach, not for..of: carrie's tsc target has no downlevel iteration.
  listeners.forEach((listener) => listener());
}

/** Record that a refusal happened. Safe to call more than once. */
export function markMfaRequired(email: string | null) {
  if (notice?.email === email) return;
  notice = { email };
  emit();
}

/** Drop the notice — the user has signed in again, so it no longer applies. */
export function clearMfaRequiredNotice() {
  if (notice === null) return;
  notice = null;
  emit();
}

export function subscribeToMfaRequiredNotice(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * The current notice, or null. Returns a stable reference until it changes,
 * which is what `useSyncExternalStore` requires.
 */
export function getMfaRequiredNotice(): MfaRequiredNotice | null {
  return notice;
}

export function useMfaRequiredNotice(): MfaRequiredNotice | null {
  return useSyncExternalStore(
    subscribeToMfaRequiredNotice,
    getMfaRequiredNotice,
    () => null,
  );
}
